import { TILE_SIZE } from "@cold-case/shared/constants";

import { buildingInteractStandPoint } from "./building-interact-stand.js";

export type WalkTileGrid = {
  isWalkableTile: (tx: number, ty: number) => boolean;
};

export type EntryArrowPlacement = {
  wx: number;
  wy: number;
  /** Chevron texture points left at rotation 0; π/2 aims tip straight up (−Y). */
  rotation: number;
};

/** Outdoor entry chevrons always point straight up (screen-space). */
export const ENTRY_ARROW_ROTATION_UP = Math.PI / 2;

/** Generated chevron texture size ([`transit-chevron.ts`](./transit-chevron.ts)). */
export const ENTRY_ARROW_TEX_W = 62;
export const ENTRY_ARROW_TEX_H = 44;

/** E-key proximity — aligned with visible chevron, not the old 0.44-tile circle. */
export function entryArrowInteractMaxDistPx(): number {
  return TILE_SIZE * 1.2;
}

export function entryArrowDisplayHitHalfExtents(
  displayWidth = ENTRY_ARROW_TEX_W,
  displayHeight = ENTRY_ARROW_TEX_H
): { halfW: number; halfH: number } {
  return { halfW: displayWidth * 0.5, halfH: displayHeight * 0.5 };
}

export type EntryArrowVisualInput = {
  anchorWorldX: number;
  anchorWorldY: number;
  footLeft: number;
  footTop: number;
  footSpan: number;
  spineWx: number;
  spineWy: number;
  /** Horizontal center of façade art (defaults to anchorWorldX). */
  facadeCenterX?: number;
  /** Bottom edge of opaque façade art in world space. */
  facadeBottomY?: number;
  /** When set, candidates overlapping opaque façade art are rejected. */
  hitsOpaque?: (wx: number, wy: number) => boolean;
};

const ENTRY_ARROW_OUTWARD_NUDGE_PX = TILE_SIZE * 0.22;

function snapToWalkable(
  mapGrid: WalkTileGrid,
  wx: number,
  wy: number,
  preferWx?: number
): { wx: number; wy: number; tx: number; ty: number } | null {
  const tx0 = Math.floor(wx / TILE_SIZE);
  const ty0 = Math.floor(wy / TILE_SIZE);
  if (mapGrid.isWalkableTile(tx0, ty0)) {
    return {
      wx: tx0 * TILE_SIZE + TILE_SIZE / 2,
      wy: ty0 * TILE_SIZE + TILE_SIZE / 2,
      tx: tx0,
      ty: ty0,
    };
  }
  let best: { wx: number; wy: number; tx: number; ty: number; drift: number } | null = null;
  for (let r = 1; r <= 4; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        const tx = tx0 + dx;
        const ty = ty0 + dy;
        if (!mapGrid.isWalkableTile(tx, ty)) continue;
        const sx = tx * TILE_SIZE + TILE_SIZE / 2;
        const sy = ty * TILE_SIZE + TILE_SIZE / 2;
        const drift =
          preferWx != null ? Math.abs(sx - preferWx) : Math.hypot(sx - wx, sy - wy);
        if (!best || drift < best.drift) {
          best = { wx: sx, wy: sy, tx, ty, drift };
        }
      }
    }
    if (best) return best;
  }
  return null;
}

function insideFootprintLot(
  wx: number,
  wy: number,
  footLeft: number,
  footTop: number,
  footSpan: number
): boolean {
  return (
    wx >= footLeft &&
    wx <= footLeft + footSpan &&
    wy >= footTop &&
    wy <= footTop + footSpan
  );
}

function isValidEntryCandidate(
  wx: number,
  wy: number,
  mapGrid: WalkTileGrid,
  visual: EntryArrowVisualInput
): boolean {
  const tx = Math.floor(wx / TILE_SIZE);
  const ty = Math.floor(wy / TILE_SIZE);
  if (!mapGrid.isWalkableTile(tx, ty)) return false;
  if (insideFootprintLot(wx, wy, visual.footLeft, visual.footTop, visual.footSpan)) return false;
  if (visual.hitsOpaque?.(wx, wy)) return false;
  const originX = visual.facadeCenterX ?? visual.anchorWorldX;
  const originY = visual.facadeBottomY ?? visual.anchorWorldY;
  const toSpineX = visual.spineWx - originX;
  const toSpineY = visual.spineWy - originY;
  const relX = wx - originX;
  const relY = wy - originY;
  return relX * toSpineX + relY * toSpineY > 0;
}

/** Smallest outward step (tile fractions) from façade bottom before leaving the placement lot. */
function minOutwardAlongTiles(
  visual: EntryArrowVisualInput,
  ux: number,
  uy: number
): number {
  const centerX = visual.facadeCenterX ?? visual.anchorWorldX;
  const bottomY = visual.facadeBottomY ?? visual.footTop + visual.footSpan * 0.92;
  const { footLeft, footTop, footSpan } = visual;
  for (let step = 0; step <= 48; step++) {
    const along = 0.08 + step * 0.1;
    const wx = centerX + ux * TILE_SIZE * along;
    const wy = bottomY + uy * TILE_SIZE * along;
    if (!insideFootprintLot(wx, wy, footLeft, footTop, footSpan)) return along;
  }
  return 4.5;
}

/**
 * Walkable sidewalk at the horizontal center of the façade bottom, nudged toward the spine.
 */
export function findEntryArrowWorldPoint(
  visual: EntryArrowVisualInput,
  mapGrid: WalkTileGrid,
  stackIndex = 0
): { wx: number; wy: number } | null {
  const { anchorWorldX, footLeft, footTop, footSpan, spineWx, spineWy } = visual;
  const centerX = visual.facadeCenterX ?? anchorWorldX;
  const bottomY = visual.facadeBottomY ?? footTop + footSpan * 0.92;
  const toSpineX = spineWx - centerX;
  const toSpineY = spineWy - bottomY;
  const toLen = Math.hypot(toSpineX, toSpineY) || 1;
  const ux = toSpineX / toLen;
  const uy = toSpineY / toLen;
  const perpX = -uy;
  const perpY = ux;

  const spreadOffsets = [0, -1, 1];
  const txBias = spreadOffsets[stackIndex % 3] ?? 0;
  const centerTx = Math.round((centerX - TILE_SIZE / 2) / TILE_SIZE);
  const startTy = Math.floor(bottomY / TILE_SIZE);
  const spineTy = Math.floor(spineWy / TILE_SIZE);
  const tyStep = Math.sign(spineTy - startTy) || 1;

  for (let ty = startTy; ty >= 0 && ty < 512; ty += tyStep) {
    const tx = centerTx + txBias;
    if (!mapGrid.isWalkableTile(tx, ty)) continue;
    const wx = tx * TILE_SIZE + TILE_SIZE / 2;
    const wy = ty * TILE_SIZE + TILE_SIZE / 2;
    if (!isValidEntryCandidate(wx, wy, mapGrid, visual)) continue;
    return { wx, wy };
  }

  // Fallback: step outward from bottom-center toward spine.
  const minAlong = minOutwardAlongTiles(visual, ux, uy);
  for (let alongStep = 0; alongStep <= 20; alongStep++) {
    const along = minAlong + alongStep * 0.25;
    const wx = centerX + ux * TILE_SIZE * along + perpX * TILE_SIZE * txBias * 0.45;
    const wy = bottomY + uy * TILE_SIZE * along + perpY * TILE_SIZE * txBias * 0.45;
    if (!isValidEntryCandidate(wx, wy, mapGrid, visual)) continue;
    const snapped = snapToWalkable(mapGrid, wx, wy, centerX);
    if (snapped && isValidEntryCandidate(snapped.wx, snapped.wy, mapGrid, visual)) {
      return { wx: snapped.wx, wy: snapped.wy };
    }
    return { wx, wy };
  }

  return null;
}

/** Rotation so chevron tip (left at 0 → tip angle rotation + π) points at target. */
export function entryArrowRotationToward(
  wx: number,
  wy: number,
  targetWx: number,
  targetWy: number
): number {
  const tipAngle = Math.atan2(targetWy - wy, targetWx - wx);
  return tipAngle - Math.PI;
}

/**
 * Walkable stand at the bottom-center of the façade — tip points straight up.
 */
export function entryArrowWorldPoint(
  footLeft: number,
  footTop: number,
  footSpan: number,
  spineWx: number,
  spineWy: number,
  mapGrid: WalkTileGrid,
  _stackIndex = 0,
  visual?: Partial<EntryArrowVisualInput>
): EntryArrowPlacement {
  const anchorWorldX = visual?.anchorWorldX ?? footLeft + footSpan / 2;
  const anchorWorldY = visual?.anchorWorldY ?? footTop + footSpan / 2;
  const input: EntryArrowVisualInput = {
    anchorWorldX,
    anchorWorldY,
    footLeft,
    footTop,
    footSpan,
    spineWx,
    spineWy,
    facadeCenterX: visual?.facadeCenterX,
    facadeBottomY: visual?.facadeBottomY,
    hitsOpaque: visual?.hitsOpaque,
  };

  const found = findEntryArrowWorldPoint(input, mapGrid, _stackIndex);
  if (found) {
    return { wx: found.wx, wy: found.wy, rotation: ENTRY_ARROW_ROTATION_UP };
  }

  const stand = buildingInteractStandPoint({
    footLeft,
    footTop,
    footSpan,
    spineWx,
    spineWy,
  });
  const snapped = snapToWalkable(mapGrid, stand.wx, stand.wy);
  const wx = snapped?.wx ?? stand.wx;
  const wy = snapped?.wy ?? stand.wy;
  return { wx, wy, rotation: ENTRY_ARROW_ROTATION_UP };
}

/** Same placement as the entry chevron — use for outdoor exit spawn. */
export function entryArrowStandWorldPoint(
  footLeft: number,
  footTop: number,
  footSpan: number,
  spineWx: number,
  spineWy: number,
  mapGrid: WalkTileGrid,
  stackIndex = 0,
  visual?: Partial<EntryArrowVisualInput>
): { wx: number; wy: number } {
  const p = entryArrowWorldPoint(
    footLeft,
    footTop,
    footSpan,
    spineWx,
    spineWy,
    mapGrid,
    stackIndex,
    visual
  );
  return { wx: p.wx, wy: p.wy };
}
